<script setup lang="ts">
// 单条消息气泡（user / assistant / tool）
//
// 渲染要点（对应设计文档 §7.3 与计划 Task 5 Step 4）：
// - assistant 正文用全局组件 md-preview；
// - thinking 折叠展示（交互范式沿用 components/markdown/AIEnhancement.vue）；
// - 工具调用过程渲染为一行状态：正在读取 → 已读取 / 失败（失败灰色提示）；
// - 只有 toolCalls 而无正文的 assistant 消息渲染为折叠的「查询过程」条目；
// - interrupted 时给出「内容可能不完整」告警。
import { computed, ref } from "vue";
import type { AiAction } from "@/apis/ai.ts";
import type { AiCoachMessage } from "@/stores/aiCoach.ts";
import AiActionCard from "@/components/ai/AiActionCard.vue";

const props = defineProps<{
  message: AiCoachMessage;
  /**
   * 需要业务处理的动作处理函数（judge_parse_save 等），由浮窗逐层传入。
   * 第二个参数是卡片所在的气泡：保存接口要的「正文」就是这条 assistant 消息的 content，
   * 由本组件在调用时绑定（浮窗拿不到「动作挂在哪个气泡」这一信息）。
   */
  saveHandler?: (action: AiAction, message: AiCoachMessage) => Promise<void> | void;
}>();

const emit = defineEmits<{
  (e: "handled", action: AiAction): void;
}>();

/** 工具 id → 中文名；未收录的工具名直接显示原名（不猜测含义） */
const TOOL_NAME_TEXT: Record<string, string> = {
  get_judge_detail: "提交详情",
  get_problem: "题目信息",
  get_user_recent_judges: "我的提交记录",
  get_problem_statistics: "题目统计",
};

interface ToolLine {
  call_id: string;
  name: string;
  state: "running" | "ok" | "failed";
  summary: string;
}

const thinkingCollapsed = ref(false);
/** 查询过程默认折叠：工具往返可能有好几轮，展开会淹没正文 */
const processCollapsed = ref(true);

const isUser = computed(() => props.message.role === "user");
const isTool = computed(() => props.message.role === "tool");

/** 工具状态行：实时消息由 toolCalls + toolResults 配对；历史工具消息直接用 toolResults */
const toolLines = computed<ToolLine[]>(() => {
  if (isTool.value) {
    return props.message.toolResults.map((item) => ({
      call_id: item.call_id,
      name: item.name ?? "",
      state: item.ok ? "ok" : "failed",
      summary: item.summary ?? "",
    }));
  }
  return props.message.toolCalls.map((call) => {
    const result = props.message.toolResults.find((item) => item.call_id === call.call_id);
    return {
      call_id: call.call_id,
      name: call.name,
      state: result ? (result.ok ? "ok" : "failed") : "running",
      summary: result?.summary ?? "",
    };
  });
});

const hasProcess = computed(() => toolLines.value.length > 0);
/** 只有工具调用、没有正文与思考：整条消息就是一次「查询过程」 */
const onlyProcess = computed(
  () => !isUser.value && !isTool.value && !props.message.content.trim() && !props.message.thinking.trim() && hasProcess.value,
);
/** 折叠状态下仍在跑的工具：把「正在读取…」提到标题行，用户不必展开就能看到进度 */
const runningLine = computed(() => toolLines.value.find((line) => line.state === "running"));

function toolNameText(name: string): string {
  return TOOL_NAME_TEXT[name] || name || "工具";
}

function toolStateText(line: ToolLine): string {
  const name = toolNameText(line.name);
  if (line.state === "running") {
    return `正在读取${name}…`;
  }
  if (line.state === "ok") {
    return `已读取${name}`;
  }
  return `读取${name}失败`;
}

/**
 * 传给动作卡片的处理函数：把当前气泡（正文来源）一并带上。
 * 未注入 saveHandler 时保持 undefined —— 只读页面（会话详情）里卡片仍按「未接入」禁用占位，
 * 不能因为这里包了一层就变成可点。
 */
const cardSaveHandler = computed(() =>
  props.saveHandler ? (action: AiAction) => props.saveHandler?.(action, props.message) : undefined,
);
</script>

<template>
  <!-- 用户消息 -->
  <div v-if="isUser" class="ai-msg ai-msg--user">
    <div class="ai-msg-user-bubble">{{ message.content }}</div>
  </div>

  <!-- 工具消息（历史回溯里找不到归属的独立条目） -->
  <div v-else-if="isTool" class="ai-msg ai-msg--tool">
    <div v-for="line in toolLines" :key="line.call_id" class="ai-tool-line" :class="`is-${line.state}`">
      <t-loading v-if="line.state === 'running'" size="small" />
      <t-icon v-else-if="line.state === 'ok'" name="check-circle" />
      <t-icon v-else name="close-circle" />
      <span class="ai-tool-line-text">{{ toolStateText(line) }}</span>
      <span v-if="line.summary" class="ai-tool-line-summary">{{ line.summary }}</span>
    </div>
  </div>

  <!-- assistant 消息 -->
  <div v-else class="ai-msg ai-msg--assistant">
    <!-- 思考过程：折叠展示 -->
    <div v-if="message.thinking" class="ai-thinking">
      <div class="ai-thinking-header" @click="thinkingCollapsed = !thinkingCollapsed">
        <t-icon name="chat" />
        <span>思考过程</span>
        <t-icon :name="thinkingCollapsed ? 'chevron-down' : 'chevron-up'" />
      </div>
      <div v-show="!thinkingCollapsed" class="ai-thinking-body">
        <pre class="ai-thinking-text">{{ message.thinking }}</pre>
      </div>
    </div>

    <!-- 正文（md-preview 是 main.ts 注册的全局组件） -->
    <div v-if="message.content" class="ai-msg-content">
      <md-preview :model-value="message.content" preview-theme="cyanosis" />
    </div>

    <!-- 仅加载态：既没有正文也没有工具调用时 -->
    <div v-if="message.generating && !message.content && !hasProcess" class="ai-msg-loading">
      <t-loading size="small" text="AI 正在思考..." />
    </div>

    <!-- 查询过程：折叠条目（只有工具调用时整条消息就是它） -->
    <div v-if="hasProcess" class="ai-process">
      <div class="ai-process-header" @click="processCollapsed = !processCollapsed">
        <t-icon name="search" />
        <span class="ai-process-title">查询过程（{{ toolLines.length }} 次工具调用）</span>
        <span v-if="processCollapsed && runningLine" class="ai-process-running">{{ toolStateText(runningLine) }}</span>
        <t-icon :name="processCollapsed ? 'chevron-down' : 'chevron-up'" />
      </div>
      <div v-show="!processCollapsed" class="ai-process-body">
        <div v-for="line in toolLines" :key="line.call_id" class="ai-tool-line" :class="`is-${line.state}`">
          <t-loading v-if="line.state === 'running'" size="small" />
          <t-icon v-else-if="line.state === 'ok'" name="check-circle" />
          <t-icon v-else name="close-circle" />
          <span class="ai-tool-line-text">{{ toolStateText(line) }}</span>
          <span v-if="line.summary" class="ai-tool-line-summary">{{ line.summary }}</span>
        </div>
      </div>
    </div>

    <!-- 中断告警：内容可能不完整 -->
    <t-alert
      v-if="message.interrupted"
      class="ai-msg-interrupted"
      theme="warning"
      :message="message.interruptedMessage || '生成被中断，内容可能不完整'"
    />

    <!-- 动作卡片：空数组不渲染任何内容 -->
    <div v-if="message.actions.length > 0" class="ai-msg-actions">
      <AiActionCard
        v-for="(action, index) in message.actions"
        :key="`${action.type}-${index}`"
        :action="action"
        :save-handler="cardSaveHandler"
        @handled="(item: AiAction) => emit('handled', item)"
      />
    </div>

    <!-- 生成中的通用提示：只有工具调用的气泡已经在标题行显示「正在读取…」，不再重复 -->
    <div v-if="message.generating && !onlyProcess && (message.content || message.thinking)" class="ai-msg-generating">
      <t-loading size="small" />
      <span>AI 正在生成中...</span>
    </div>
  </div>
</template>

<style scoped>
.ai-msg {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}

/* 用户消息：右对齐气泡 */
.ai-msg--user {
  align-items: flex-end;
}

.ai-msg-user-bubble {
  max-width: 85%;
  padding: 8px 12px;
  border-radius: var(--td-radius-default);
  background-color: var(--td-brand-color-light);
  color: var(--td-text-color-primary);
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

/* assistant 消息：左对齐，正文交给 md-preview */
.ai-msg--assistant {
  align-items: stretch;
}

.ai-msg-content {
  font-size: 13px;
  line-height: 1.7;
  color: var(--td-text-color-primary);
  overflow-x: auto;
}

.ai-msg-loading,
.ai-msg-generating {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  color: var(--td-text-color-secondary);
  font-size: 12px;
}

/* 思考过程：与 AIEnhancement.vue 的交互一致 */
.ai-thinking {
  margin-bottom: 8px;
  border: 1px solid var(--td-component-border);
  border-radius: var(--td-radius-default);
  overflow: hidden;
}

.ai-thinking-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background-color: var(--td-bg-color-secondarycontainer);
  cursor: pointer;
  font-size: 12px;
  color: var(--td-text-color-secondary);
  user-select: none;
}

.ai-thinking-header span {
  flex: 1;
}

.ai-thinking-body {
  padding: 10px;
  background-color: var(--td-bg-color-container-hover);
}

.ai-thinking-text {
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  color: var(--td-text-color-secondary);
  font-family: inherit;
}

/* 查询过程：折叠条目 */
.ai-process {
  margin: 6px 0;
  border: 1px solid var(--td-component-border);
  border-radius: var(--td-radius-default);
  overflow: hidden;
}

.ai-process-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background-color: var(--td-bg-color-secondarycontainer);
  cursor: pointer;
  font-size: 12px;
  color: var(--td-text-color-secondary);
  user-select: none;
}

.ai-process-title {
  flex: none;
}

.ai-process-running {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--td-text-color-placeholder);
}

.ai-process-header .t-icon:last-child {
  margin-left: auto;
}

.ai-process-body {
  padding: 8px 10px;
  background-color: var(--td-bg-color-container-hover);
}

/* 工具状态行 */
.ai-tool-line {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 1.8;
  color: var(--td-text-color-secondary);
}

.ai-tool-line.is-running {
  color: var(--td-text-color-secondary);
}

.ai-tool-line.is-ok {
  color: var(--td-text-color-placeholder);
}

/* 失败：灰色提示（不当作错误打断对话） */
.ai-tool-line.is-failed {
  color: var(--td-text-color-placeholder);
}

.ai-tool-line-text {
  flex: none;
}

.ai-tool-line-summary {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--td-text-color-placeholder);
}

.ai-msg--tool {
  padding-left: 2px;
}

.ai-msg-interrupted {
  margin: 6px 0;
}

.ai-msg-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
</style>
